import { useEffect, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AlertTriangle, CalendarClock, Check, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useActiveUnitId } from '@/contexts/ActiveUnitContext';
import { useActivePeriodoLetivo } from '@/contexts/ActivePeriodoLetivoContext';
import { usePeriodosLetivos, type PeriodoLetivo } from '@/hooks/usePeriodosLetivos';
import type { MotivoSelecaoPeriodo } from '@/utils/periodoLetivoAtual';
import { notifyError, notifySuccess, notifyWarning } from '@/utils/notify';
import { cn } from '@/lib/utils';

const rotuloMotivo: Record<MotivoSelecaoPeriodo, string> = {
  vigente: 'Vigente',
  proximo: 'Próximo',
  encerrado: 'Encerrado',
  indefinido: 'Indefinido',
};

const corBadgeAuto: Record<MotivoSelecaoPeriodo, string> = {
  vigente: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  proximo: 'border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  encerrado: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  indefinido: 'border-muted-foreground/30 bg-muted text-muted-foreground',
};

export const ActivePeriodoLetivoSwitcher = () => {
  const activeUnitId = useActiveUnitId();
  const {
    periodoAtivo,
    origem,
    motivo,
    isLoading,
    isError,
    setPeriodoOverride,
    resetOverride,
    refetch,
    isRefreshing,
    refreshAttempt,
    refreshMaxAttempts,
    lastRefreshError,
    lastRefreshedAt,
    refresh,
    clearRefreshError,
    realtimeStatus,
    realtimeError,
    reconnectRealtime,
  } = useActivePeriodoLetivo();

  const { data: periodosAbertos = [] } = usePeriodosLetivos(
    { unidadeId: activeUnitId ?? null, status: 'aberto', ativo: true },
    { enabled: !!activeUnitId },
  );
  const { data: periodosEncerrados = [] } = usePeriodosLetivos(
    { unidadeId: activeUnitId ?? null, status: 'encerrado', ativo: true },
    { enabled: !!activeUnitId },
  );

  const encerradosRecentes = useMemo(() => {
    const seisMesesAtras = new Date();
    seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6);
    return (periodosEncerrados ?? [])
      .filter((p: PeriodoLetivo) => {
        const fim = p.data_fim ? new Date(`${p.data_fim}T00:00:00`) : null;
        return fim ? fim >= seisMesesAtras : false;
      })
      .slice(0, 6);
  }, [periodosEncerrados]);

  const jaAvisouErro = useRef(false);
  const jaAvisouVazio = useRef(false);
  const tinhaErroRefresh = useRef(false);

  useEffect(() => {
    if (isError && !jaAvisouErro.current) {
      jaAvisouErro.current = true;
      notifyError(
        'Não foi possível resolver o período letivo ativo',
        'Verifique sua conexão e tente novamente.',
      );
    }
    if (!isError) jaAvisouErro.current = false;
  }, [isError]);

  useEffect(() => {
    if (isLoading || isError) return;
    if (!periodoAtivo && !jaAvisouVazio.current) {
      jaAvisouVazio.current = true;
      notifyWarning(
        'Nenhum período letivo encontrado',
        'Cadastre ou abra um período em Parâmetros Acadêmicos → Períodos Letivos.',
      );
    }
    if (periodoAtivo) jaAvisouVazio.current = false;
  }, [isLoading, isError, periodoAtivo]);

  // Notifica erro/recuperação do refresh manual
  useEffect(() => {
    if (lastRefreshError) {
      tinhaErroRefresh.current = true;
      notifyError('Falha ao atualizar', lastRefreshError.message);
    } else if (tinhaErroRefresh.current && !isRefreshing) {
      tinhaErroRefresh.current = false;
      notifySuccess('Período letivo atualizado.');
    }
  }, [lastRefreshError, isRefreshing]);

  const handleAtualizar = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearRefreshError();
    void refresh();
  };

  const handleAcaoErro = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (lastRefreshError?.kind === 'permission') {
      try { await supabase.auth.refreshSession(); } catch { /* noop */ }
    } else if (lastRefreshError?.kind === 'realtime') {
      reconnectRealtime();
    }
    clearRefreshError();
    void refresh();
  };

  const rotuloAcaoErro = lastRefreshError?.kind === 'permission'
    ? 'Recarregar sessão'
    : lastRefreshError?.kind === 'realtime'
      ? 'Reconectar'
      : 'Tentar novamente';

  if (isLoading) {
    return <Skeleton className="h-9 w-40" />;
  }

  if (isError) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-9 gap-2"
        onClick={() => { void refresh(); }}
        title="Erro ao carregar período — clique para tentar novamente"
      >
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <span className="text-sm">Período indisponível</span>
        <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>
    );
  }

  const badgeLabel = origem === 'manual' ? 'Manual' : (motivo ? rotuloMotivo[motivo] : 'Auto');
  const badgeClass =
    origem === 'manual'
      ? 'border-muted-foreground/30 bg-muted text-muted-foreground'
      : motivo
        ? corBadgeAuto[motivo]
        : corBadgeAuto.indefinido;

  const renderItem = (p: PeriodoLetivo) => {
    const selecionado = p.id === periodoAtivo?.id;
    return (
      <DropdownMenuItem
        key={p.id}
        onSelect={() => setPeriodoOverride(p)}
        className="flex items-start gap-2"
      >
        <Check className={cn('mt-0.5 h-4 w-4 shrink-0', selecionado ? 'opacity-100' : 'opacity-0')} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{p.codigo ?? p.nome}</div>
          <div className="truncate text-xs text-muted-foreground">
            {p.data_inicio} → {p.data_fim}
            {p.unidade_escolar_id === null && ' · geral'}
          </div>
        </div>
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 max-w-[240px]"
          title="Período letivo ativo"
        >
          <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm">
            {periodoAtivo?.codigo ?? periodoAtivo?.nome ?? 'Sem período'}
          </span>
          <Badge variant="outline" className={cn('ml-1 h-5 px-1.5 text-[10px] font-medium', badgeClass)}>
            {badgeLabel}
          </Badge>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Período letivo</span>
          <div className="flex items-center gap-2">
            {origem === 'manual' && (
              <button
                type="button"
                onClick={() => resetOverride()}
                className="flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="h-3 w-3" /> Auto
              </button>
            )}
            <button
              type="button"
              onClick={handleAtualizar}
              disabled={isRefreshing}
              className="flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground disabled:opacity-60"
              title={
                lastRefreshedAt
                  ? `Última atualização: ${format(lastRefreshedAt, "dd/MM 'às' HH:mm:ss", { locale: ptBR })}`
                  : 'Atualizar'
              }
            >
              {isRefreshing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              {isRefreshing
                ? `Atualizando… (${refreshAttempt}/${refreshMaxAttempts})`
                : 'Atualizar'}
            </button>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {realtimeStatus !== 'subscribed' && realtimeStatus !== 'connecting' && (
          <div className="mx-2 mb-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-400">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">Atualizações em tempo real indisponíveis</div>
                {realtimeError && <div className="opacity-70 truncate">{realtimeError}</div>}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); reconnectRealtime(); }}
                  className="mt-1 inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 font-medium hover:bg-amber-500/20"
                >
                  <RefreshCw className="h-3 w-3" /> Reconectar
                </button>
              </div>
            </div>
          </div>
        )}

        {lastRefreshError && (
          <div className="mx-2 mb-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{lastRefreshError.message}</div>
                {lastRefreshError.detail && (
                  <div className="opacity-70 truncate" title={lastRefreshError.detail}>
                    {lastRefreshError.detail}
                  </div>
                )}
                <div className="opacity-70">
                  {lastRefreshError.attempts} tentativa(s) realizadas.
                </div>
                <button
                  type="button"
                  onClick={handleAcaoErro}
                  className="mt-1 inline-flex items-center gap-1 rounded bg-destructive/10 px-2 py-0.5 font-medium hover:bg-destructive/20"
                >
                  <RefreshCw className="h-3 w-3" /> {rotuloAcaoErro}
                </button>
              </div>
            </div>
          </div>
        )}

        {periodosAbertos.length === 0 && encerradosRecentes.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            Nenhum período disponível nesta unidade. Cadastre em Parâmetros Acadêmicos.
          </div>
        )}

        {periodosAbertos.length > 0 && (
          <>
            <div className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Abertos
            </div>
            {periodosAbertos.map(renderItem)}
          </>
        )}

        {encerradosRecentes.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Encerrados recentes
            </div>
            {encerradosRecentes.map(renderItem)}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
